import { Box, Paper, Stack, Typography } from '@mui/material';
import { formatMoney, type FormatMoneyArg } from '../../utils/currency';

const DEDUCTIONS = new Set(['discount', 'couponDiscount', 'walletApplied']);

function labelFor(key: string) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
}

export default function AmountBreakdown({
  fare,
  total,
  paymentMethod,
  paymentStatus,
  currency,
}: {
  fare: Record<string, unknown>;
  total: number;
  paymentMethod?: string;
  paymentStatus?: string;
  /** Currency code or the booking record; falls back to the tenant currency. */
  currency?: FormatMoneyArg;
}) {
  const lines = Object.entries(fare)
    .filter(([k, v]) => k !== 'total' && typeof v === 'number' && v !== 0)
    .map(([k, v]) => ({ key: k, label: labelFor(k), amount: DEDUCTIONS.has(k) ? -Math.abs(v as number) : (v as number) }));

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: lines.length ? 1 : 0 }}>
        <Typography variant="subtitle2" fontWeight={800}>Total amount</Typography>
        <Typography variant="h6" fontWeight={800}>{formatMoney(total, currency)}</Typography>
      </Stack>
      {lines.map((l) => (
        <Stack key={l.key} direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
          <Typography variant="body2" color="text.secondary">{l.label}</Typography>
          <Typography variant="body2" color={l.amount < 0 ? 'success.main' : undefined}>
            {l.amount < 0 ? `− ${formatMoney(Math.abs(l.amount), currency)}` : formatMoney(l.amount, currency)}
          </Typography>
        </Stack>
      ))}
      {(paymentMethod || paymentStatus) && (
        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {[paymentMethod && `Paid via ${paymentMethod}`, paymentStatus && `Payment status: ${paymentStatus.replace(/_/g, ' ')}`]
              .filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
